import { isShareableLandingPath, Paths, SEARCH_PARAMS, STORAGE_KEY } from "@constants";
import { safeSessionGetItemWithSource, safeSessionRemoveItem, safeSessionSetItem } from "@utils/storage";
import { generatePath } from "react-router-dom";

export function buildMemberReferralAbsoluteUrl(
  refCode: string | undefined | null,
  destination?: string | null,
): string {
  const siteUrl = `${window.location.protocol}//${window.location.host}`;
  const code = refCode?.trim() || "????";
  const url = siteUrl + generatePath(Paths.PUBLIC.REFERRAL_CALLBACK, { refCode: code });
  if (isShareableLandingPath(destination)) {
    const params = new URLSearchParams();
    params.set(SEARCH_PARAMS.NEXT, destination);
    return `${url}?${params.toString()}`;
  }
  return url;
}

export type ReferralCodeSource = "session" | "memory" | "url" | "none";

/** Latched on use or reject, so the URL fallback cannot resurrect the code. */
let consumed = false;

const stripReferralCodeFromUrl = () => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(SEARCH_PARAMS.REF_CODE)) return;
  url.searchParams.delete(SEARCH_PARAMS.REF_CODE);
  // Keep history.state: react-router stores key/idx there
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
};

export const storeReferralCode = (refCode: string | undefined | null) => {
  if (refCode && !consumed) {
    safeSessionSetItem(STORAGE_KEY.REF_CODE, refCode);
  }
};

export const resolveReferralCode = (): { code?: string; source: ReferralCodeSource } => {
  if (!consumed) {
    const { value: stored, fromMirror } = safeSessionGetItemWithSource(STORAGE_KEY.REF_CODE);
    if (stored) return { code: stored, source: fromMirror ? "memory" : "session" };
    const fromUrl = new URLSearchParams(window.location.search).get(SEARCH_PARAMS.REF_CODE);
    if (fromUrl) return { code: fromUrl, source: "url" };
  }
  return { source: "none" };
};

export const getReferralCode = () => resolveReferralCode().code;

export const clearReferralCode = () => {
  consumed = true;
  safeSessionRemoveItem(STORAGE_KEY.REF_CODE);
  stripReferralCodeFromUrl();
};
